import { Fragment, type ReactNode } from "react";

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const parts = text.split(/(\*\*.+?\*\*|\*[^*\n]+?\*)/g);
  return parts.map((p, i) => {
    const key = `${keyPrefix}-i${i}`;
    if (p.length > 4 && p.startsWith("**") && p.endsWith("**")) {
      return <strong key={key} className="font-semibold">{p.slice(2, -2)}</strong>;
    }
    if (p.length > 2 && p.startsWith("*") && p.endsWith("*")) {
      return <em key={key}>{p.slice(1, -1)}</em>;
    }
    return <Fragment key={key}>{p}</Fragment>;
  });
}

/** Markdown-lite renderer for timeline/note/email bodies: **bold**, *italic*,
    "- " bullets, "1. " numbered lists, blank-line paragraphs. Anything else
    renders as plain text, so old entries look exactly as before. */
export function RichText({ text, className }: { text: string; className?: string }) {
  const lines = (text || "").split("\n");
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;
  const isBullet = (l: string) => /^\s*[-•]\s+/.test(l);
  const isOrdered = (l: string) => /^\s*\d+[.)]\s+/.test(l);
  const stripBullet = (l: string) => l.replace(/^\s*[-•]\s+/, "");
  const stripOrdered = (l: string) => l.replace(/^\s*\d+[.)]\s+/, "");
  while (i < lines.length) {
    if (!lines[i].trim()) { i++; continue; }
    if (isBullet(lines[i])) {
      const items: ReactNode[] = [];
      while (i < lines.length && isBullet(lines[i])) {
        items.push(<li key={`li${key++}`}>{renderInline(stripBullet(lines[i]), `b${key}`)}</li>);
        i++;
      }
      blocks.push(<ul key={`ul${key++}`} className="list-disc pl-5">{items}</ul>);
      continue;
    }
    if (isOrdered(lines[i])) {
      const items: ReactNode[] = [];
      while (i < lines.length && isOrdered(lines[i])) {
        items.push(<li key={`li${key++}`}>{renderInline(stripOrdered(lines[i]), `b${key}`)}</li>);
        i++;
      }
      blocks.push(<ol key={`ol${key++}`} className="list-decimal pl-5">{items}</ol>);
      continue;
    }
    const para: ReactNode[] = [];
    while (i < lines.length && lines[i].trim() && !isBullet(lines[i]) && !isOrdered(lines[i])) {
      if (para.length > 0) para.push(<br key={`br${key++}`} />);
      para.push(<Fragment key={`t${key++}`}>{renderInline(lines[i], `p${key}`)}</Fragment>);
      i++;
    }
    blocks.push(<p key={`p${key++}`}>{para}</p>);
  }
  return <div className={className} style={{ overflowWrap: "anywhere" }}>{blocks}</div>;
}
